import {
  RecommendationBecauseYouWatchedTray,
  RecommendationBecauseYouWatchGenreTray,
  RecommendationCommonTray,
  RecommendationDontMissTray,
  RecommendationEditorialTray,
  RecommendationPeopleLikeYouWatchMoviesTray,
  RecommendationPeopleLikeYouWatchSeriesTray,
  RecommendationPeopleLikeYouWatchTray,
  RecommendationTopPicksMoviesTray,
  RecommendationTopPicksSeriesTray,
  RecommendationTopPicksTray,
  RecommendationTray,
  RecommendationTrayType,
  RecommendationTrendingMoviesTray,
  RecommendationTrendingSeriesTray,
  RecommendationTrendingTray,
  RecommendationTrendingWithLayoutTray,
} from '../../../domain/recommendation-tray';
import {
  CreateRecommendationPageItemRequestApiModel,
  RecommendationPageItemApiModel,
  RecommendationTypeApiModel,
  UpdateRecommendationPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  layoutMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  platformTargetingMapper,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  commonMapper,
  layoutMapperApiToDomain,
  nameLocalizedMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import {
  RecommendationBecauseYouWatchedTrayFormViewModel,
  RecommendationBecauseYouWatchGenreTrayFormViewModel,
  RecommendationCommonTrayFormViewModel,
  RecommendationEditorialTrayFormViewModel,
  RecommendationTrayTypeVM,
  RecommendationTrayViewModel,
} from './recommendation-tray-form.view-model';
import { becauseYouWatchedRecencyApiToDomain, becauseYouWatchedRecencyDomainToApi } from '../../../shared/functions/enum-mappings';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { layoutMapper, targetingMapper } from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { commonMapperViewModelToDomain } from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'Recommendation'

/**
 * DOMAIN -> API
 */

const mapDomainRecommendationTrayTypeToApi = (type: RecommendationTrayType): RecommendationTypeApiModel => {
  switch (type) {
    case 'TopPicks':
      return RecommendationTypeApiModel.PersonalizedSwimLane;
    case 'TopPicksMovies':
      return RecommendationTypeApiModel.PersonalizedSwimLaneMoviesOnly;
    case 'TopPicksSeries':
      return RecommendationTypeApiModel.PersonalizedSwimLaneSeriesOnly;
    case 'DontMiss':
      return RecommendationTypeApiModel.DontMiss;
    case 'BecauseYouWatched':
      return RecommendationTypeApiModel.BecauseYouWatched;
    case 'PeopleLikeYouWatch':
      return RecommendationTypeApiModel.BecauseOthersWatched;
    case 'TrendingWithLayout':
      return RecommendationTypeApiModel.TrendingWithLayout;
    case 'PeopleLikeYouWatchMovies':
      return RecommendationTypeApiModel.MoviesOthersLikeYouWatched;
    case 'PeopleLikeYouWatchSeries':
      return RecommendationTypeApiModel.SeriesOthersLikeYouWatched;
    case 'Trending': // AXB-929
      return RecommendationTypeApiModel.Trending;
    case 'TrendingMovies': // AXB-930
      return RecommendationTypeApiModel.TrendingMovies;
    case 'TrendingSeries': // AXB-931
      return RecommendationTypeApiModel.TrendingSeries;
    case 'BecauseYouWatchGenre':
      return RecommendationTypeApiModel.BecauseYouWatchedGenre;
    case 'TopPicksDownloadable':
      return RecommendationTypeApiModel.PersonalizedSwimLaneDownloadable;
    case 'TopPicksMoviesDownloadable':
      return RecommendationTypeApiModel.PersonalizedSwimLaneMoviesOnlyDownloadable;
    case 'TopPicksSeriesDownloadable':
      return RecommendationTypeApiModel.PersonalizedSwimLaneSeriesOnlyDownloadable;
    case 'PeopleLikeYouWatchDownloadable':
      return RecommendationTypeApiModel.BecauseOthersWatchedDownloadable;
    case 'PeopleLikeYouWatchMoviesDownloadable':
      return RecommendationTypeApiModel.MoviesOthersLikeYouWatchedDownloadable;
    case 'PeopleLikeYouWatchSeriesDownloadable':
      return RecommendationTypeApiModel.SeriesOthersLikeYouWatchedDownloadable;
    case 'MostDownloadedMovies':
      return RecommendationTypeApiModel.MostDownloadedMovies;
    case 'MostDownloadedSeries':
      return RecommendationTypeApiModel.MostDownloadedSeries;
  }
};

export const mapRecommendationTrayDomainModelToApiModel = (
  recommendationTray: RecommendationTray,
  pageVersionId?: string,
  tenantId?: string
): CreateRecommendationPageItemRequestApiModel & UpdateRecommendationPageItemRequestApiModel => {
  const commonProperties = mapperPipe(
    recommendationTray,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    layoutMapperDomainToApi,
    platformTargetingMapper
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    programTypes: [],
    contentTypes: [],
    isKidsOnly: recommendationTray.isKidsOnly,
    recommendationType:
      recommendationTray.recommendationTrayType === 'Editorial'
        ? recommendationTray.editorialUC.useCaseId
        : mapDomainRecommendationTrayTypeToApi(recommendationTray.recommendationTrayType),
    recency:
      recommendationTray.recommendationTrayType === 'BecauseYouWatched' ||
      recommendationTray.recommendationTrayType === 'BecauseYouWatchGenre'
        ? recommendationTray.recency
        : undefined,
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

const mapApiRecommendationTypeToDomain = (type: string): RecommendationTrayType => {
  switch (type) {
    case RecommendationTypeApiModel.PersonalizedSwimLane:
      return 'TopPicks';
    case RecommendationTypeApiModel.PersonalizedSwimLaneMoviesOnly:
      return 'TopPicksMovies';
    case RecommendationTypeApiModel.PersonalizedSwimLaneSeriesOnly:
      return 'TopPicksSeries';
    case RecommendationTypeApiModel.DontMiss:
      return 'DontMiss';
    case RecommendationTypeApiModel.BecauseOthersWatched:
      return 'PeopleLikeYouWatch';
    case RecommendationTypeApiModel.MoviesOthersLikeYouWatched:
      return 'PeopleLikeYouWatchMovies';
    case RecommendationTypeApiModel.SeriesOthersLikeYouWatched:
      return 'PeopleLikeYouWatchSeries';
    case RecommendationTypeApiModel.BecauseYouWatched:
      return 'BecauseYouWatched';
    case RecommendationTypeApiModel.Trending: // AXB-929
      return 'Trending';
    case RecommendationTypeApiModel.TrendingMovies: // AXB-930
      return 'TrendingMovies';
    case RecommendationTypeApiModel.TrendingSeries: // AXB-931
      return 'TrendingSeries';
    case RecommendationTypeApiModel.TrendingWithLayout:
      return 'TrendingWithLayout';
    case RecommendationTypeApiModel.BecauseYouWatchedGenre:
      return 'BecauseYouWatchGenre';
    case RecommendationTypeApiModel.MostDownloadedSeries:
      return 'MostDownloadedSeries';
    case RecommendationTypeApiModel.MostDownloadedMovies:
      return 'MostDownloadedMovies';
    case RecommendationTypeApiModel.PersonalizedSwimLaneDownloadable:
      return 'TopPicksDownloadable';
    case RecommendationTypeApiModel.PersonalizedSwimLaneMoviesOnlyDownloadable:
      return 'TopPicksMoviesDownloadable';
    case RecommendationTypeApiModel.PersonalizedSwimLaneSeriesOnlyDownloadable:
      return 'TopPicksSeriesDownloadable';
    case RecommendationTypeApiModel.BecauseOthersWatchedDownloadable:
      return 'PeopleLikeYouWatchDownloadable';
    case RecommendationTypeApiModel.SeriesOthersLikeYouWatchedDownloadable:
      return 'PeopleLikeYouWatchSeriesDownloadable';
    case RecommendationTypeApiModel.MoviesOthersLikeYouWatchedDownloadable:
      return 'PeopleLikeYouWatchMoviesDownloadable';
    default:
      return 'Editorial';
  }
};

export const mapRecommendationTrayApiModelToDomainModel = (recommendation: RecommendationPageItemApiModel): RecommendationTray => {
  const domainModel: RecommendationCommonTray = remainderMapper(
    mapperPipe(
      recommendation,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      layoutMapperApiToDomain,
      platformTargetingMapper
    ),
    () => ({
      type: 'Recommendation',
      isKidsOnly: recommendation.isKidsOnly,
      recommendationTrayType: mapApiRecommendationTypeToDomain(recommendation.recommendationType),
    })
  );

  switch (domainModel.recommendationTrayType) {
    case 'BecauseYouWatched':
    case 'BecauseYouWatchGenre': // AXB-932
      return {
        ...domainModel,
        recency: recommendation.recency,
      } as RecommendationBecauseYouWatchedTray | RecommendationBecauseYouWatchGenreTray;
    case 'PeopleLikeYouWatch':
      return {
        ...domainModel,
        program: undefined,
      } as RecommendationPeopleLikeYouWatchTray;
    case 'Editorial':
      return {
        ...domainModel,
        editorialUC: { useCaseId: recommendation.recommendationType },
      } as RecommendationEditorialTray;
    default:
      return domainModel as RecommendationTopPicksTray | RecommendationTrendingTray | RecommendationBecauseYouWatchGenreTray;
  }
};

/**
 * DOMAIN -> VIEWMODEL
 */

const mapDomainRecoTypeToVM = (
  type: RecommendationTrayType
):
  | TrayFormViewModelType.PeopleLikeYouWatch
  | TrayFormViewModelType.BecauseYouWatched
  | TrayFormViewModelType.TopPicks
  | TrayFormViewModelType.Trending // AXB-929
  | TrayFormViewModelType.TrendingMovies // AXB-930
  | TrayFormViewModelType.TrendingSeries // AXB-931
  | TrayFormViewModelType.TrendingWithLayout // AXB-929
  | TrayFormViewModelType.BecauseYouWatchGenreTray
  | TrayFormViewModelType.PeopleLikeYouWatchDownloadable
  | TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable
  | TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable
  | TrayFormViewModelType.TopPicksDownloadable
  | TrayFormViewModelType.TopPicksMoviesDownloadable
  | TrayFormViewModelType.TopPicksSeriesDownloadable
  | TrayFormViewModelType.MostDownloadedMovies
  | TrayFormViewModelType.MostDownloadedSeries
  | TrayFormViewModelType.Editorial => {
  switch (type) {
    case 'PeopleLikeYouWatch':
      return TrayFormViewModelType.PeopleLikeYouWatch;
    case 'BecauseYouWatched':
      return TrayFormViewModelType.BecauseYouWatched;
    case 'TopPicks':
      return TrayFormViewModelType.TopPicks;
    case 'Trending': // AXB-929
      return TrayFormViewModelType.Trending;
    case 'TrendingMovies': // AXB-930
      return TrayFormViewModelType.TrendingMovies;
    case 'TrendingSeries': // AXB-931
      return TrayFormViewModelType.TrendingSeries;
    case 'TrendingWithLayout': // AXB-929
      return TrayFormViewModelType.TrendingWithLayout;
    case 'BecauseYouWatchGenre':
      return TrayFormViewModelType.BecauseYouWatchGenreTray;
    case 'TopPicksDownloadable':
      return TrayFormViewModelType.TopPicksDownloadable;
    case 'TopPicksMoviesDownloadable':
      return TrayFormViewModelType.TopPicksMoviesDownloadable;
    case 'TopPicksSeriesDownloadable':
      return TrayFormViewModelType.TopPicksSeriesDownloadable;
    case 'PeopleLikeYouWatchDownloadable':
      return TrayFormViewModelType.PeopleLikeYouWatchDownloadable;
    case 'PeopleLikeYouWatchMoviesDownloadable':
      return TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable;
    case 'PeopleLikeYouWatchSeriesDownloadable':
      return TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable;
    case 'MostDownloadedMovies':
      return TrayFormViewModelType.MostDownloadedMovies;
    case 'MostDownloadedSeries':
      return TrayFormViewModelType.MostDownloadedSeries;
    case 'Editorial':
      return TrayFormViewModelType.Editorial;
  }
};

export const mapRecommendationTrayDomainModelToViewModel = (domainModel: RecommendationTray): RecommendationTrayViewModel => {
  const commonProperties = mapperPipe(domainModel, commonMapper, targetingMapper, layoutMapper, platformTargetingMapper);

  const commonRecommendationTrayData: RecommendationCommonTrayFormViewModel = {
    title: { en: domainModel.titleEnglish, ar: domainModel.titleArabic },
    isKidsOnly: domainModel.isKidsOnly,
    type: mapDomainRecoTypeToVM(domainModel.recommendationTrayType),
    ...commonProperties,
  };
  switch (domainModel.recommendationTrayType) {
    case 'BecauseYouWatched':
      return {
        ...commonRecommendationTrayData,
        recency: becauseYouWatchedRecencyApiToDomain[domainModel.recency], // AXB-925
        type: TrayFormViewModelType.BecauseYouWatched, // AXB-925
      } as RecommendationBecauseYouWatchedTrayFormViewModel;
    case 'BecauseYouWatchGenre': // AXB-932
      return {
        ...commonRecommendationTrayData,
        recency: becauseYouWatchedRecencyApiToDomain[domainModel.recency], // AXB-925
        type: TrayFormViewModelType.BecauseYouWatchGenreTray, // AXB-925
      } as RecommendationBecauseYouWatchGenreTrayFormViewModel; // AXB-932
    case 'PeopleLikeYouWatch':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatch };
    case 'PeopleLikeYouWatchMovies':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatchMovies };
    case 'PeopleLikeYouWatchSeries':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatchSeries };
    case 'TopPicks':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicks }; // AXB-1061
    case 'TopPicksMovies':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicksMovies }; // AXB-1062
    case 'TopPicksSeries':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicksSeries };
    case 'DontMiss':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.DontMiss };
    case 'TrendingWithLayout':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TrendingWithLayout }; // AXB-929
    case 'Trending':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.Trending }; // AXB-929
    case 'TrendingMovies':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TrendingMovies }; // AXB-930
    case 'TrendingSeries':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TrendingSeries }; // AXB-931
    case 'TopPicksDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicksDownloadable };
    case 'TopPicksMoviesDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicksMoviesDownloadable };
    case 'TopPicksSeriesDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.TopPicksSeriesDownloadable };
    case 'PeopleLikeYouWatchDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatchDownloadable };
    case 'PeopleLikeYouWatchMoviesDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable };
    case 'PeopleLikeYouWatchSeriesDownloadable':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable };
    case 'MostDownloadedMovies':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.MostDownloadedMovies };
    case 'MostDownloadedSeries':
      return { ...commonRecommendationTrayData, type: TrayFormViewModelType.MostDownloadedSeries };
    case 'Editorial':
      return {
        ...commonRecommendationTrayData,
        type: TrayFormViewModelType.Editorial,
        editorialUC: { ...domainModel.editorialUC },
      } as RecommendationEditorialTrayFormViewModel;
  }
};

/**
 * VIEWMODEL -> DOMAIN
 */

const mapViewModelRecommendationTypeToDomain = (type: RecommendationTrayTypeVM): RecommendationTrayType => {
  switch (type) {
    case TrayFormViewModelType.BecauseYouWatched:
      return 'BecauseYouWatched';
    case TrayFormViewModelType.Trending: // AXB-929
      return 'Trending';
    case TrayFormViewModelType.TrendingMovies: // AXB-930
      return 'TrendingMovies';
    case TrayFormViewModelType.TrendingSeries: // AXB-931
      return 'TrendingSeries';
    case TrayFormViewModelType.TrendingWithLayout: // AXB-929
      return 'TrendingWithLayout';
    case TrayFormViewModelType.TopPicks: // AXB-1061
      return 'TopPicks';
    case TrayFormViewModelType.TopPicksMovies: // AXB-1062
      return 'TopPicksMovies';
    case TrayFormViewModelType.TopPicksSeries:
      return 'TopPicksSeries';
    case TrayFormViewModelType.DontMiss:
      return 'DontMiss';
    case TrayFormViewModelType.PeopleLikeYouWatch:
      return 'PeopleLikeYouWatch';
    case TrayFormViewModelType.PeopleLikeYouWatchMovies:
      return 'PeopleLikeYouWatchMovies';
    case TrayFormViewModelType.PeopleLikeYouWatchSeries:
      return 'PeopleLikeYouWatchSeries';
    case TrayFormViewModelType.BecauseYouWatchGenreTray:
      return 'BecauseYouWatchGenre';
    case TrayFormViewModelType.PeopleLikeYouWatchDownloadable:
      return 'PeopleLikeYouWatchDownloadable';
    case TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable:
      return 'PeopleLikeYouWatchMoviesDownloadable';
    case TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable:
      return 'PeopleLikeYouWatchSeriesDownloadable';
    case TrayFormViewModelType.TopPicksDownloadable:
      return 'TopPicksDownloadable';
    case TrayFormViewModelType.TopPicksMoviesDownloadable:
      return 'TopPicksMoviesDownloadable';
    case TrayFormViewModelType.TopPicksSeriesDownloadable:
      return 'TopPicksSeriesDownloadable';
    case TrayFormViewModelType.MostDownloadedMovies:
      return 'MostDownloadedMovies';
    case TrayFormViewModelType.MostDownloadedSeries:
      return 'MostDownloadedSeries';
    case TrayFormViewModelType.Editorial:
      return 'Editorial';
  }
};

export const mapRecommendationTrayViewModelToDomainModel = (vm: RecommendationTrayViewModel): RecommendationTray => {
  const commonProperties = mapperPipe(vm, commonMapperViewModelToDomain, targetingMapper, layoutMapper, platformTargetingMapper);
  const commonRecommendationTrayData: RecommendationCommonTray = {
    titleEnglish: vm.title.en,
    titleArabic: vm.title.ar,
    isKidsOnly: vm.isKidsOnly,
    type: 'Recommendation',
    recommendationTrayType: mapViewModelRecommendationTypeToDomain(vm.type),
    ...commonProperties,
  };
  switch (vm.type) {
    case TrayFormViewModelType.BecauseYouWatched:
    case TrayFormViewModelType.BecauseYouWatchGenreTray: // AXB-932
      return {
        ...commonRecommendationTrayData,
        recency: becauseYouWatchedRecencyDomainToApi[vm.recency], // AXB-925,
      } as RecommendationBecauseYouWatchedTray | RecommendationBecauseYouWatchGenreTray; // AXB-932
    case TrayFormViewModelType.Editorial:
      return {
        ...commonRecommendationTrayData,
        editorialUC: { ...vm.editorialUC },
      } as RecommendationEditorialTray;
    default:
      return commonRecommendationTrayData as
        | RecommendationTrendingTray
        | RecommendationTrendingMoviesTray
        | RecommendationTrendingSeriesTray
        | RecommendationTrendingWithLayoutTray
        | RecommendationTopPicksTray
        | RecommendationTopPicksSeriesTray
        | RecommendationTopPicksMoviesTray
        | RecommendationPeopleLikeYouWatchMoviesTray
        | RecommendationPeopleLikeYouWatchSeriesTray
        | RecommendationDontMissTray
        | RecommendationPeopleLikeYouWatchTray;
  }
};
